import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { CalculatorFormState, ROIResults } from '../types';
import { formatCurrency, formatPercentage } from '../utils/formatters';

interface ResultsLocationState {
    results?: ROIResults;
    inputs?: CalculatorFormState;
}

const Results: React.FC = () => {
    const location = useLocation<ResultsLocationState>();
    const history = useHistory();
    const results = location.state?.results;
    const inputs = location.state?.inputs;

    if (!results || !inputs) {
        return (
            <section className="results-page">
                <h1>ROI Results</h1>
                <p>No calculation data found. Start by running a scenario.</p>
                <button type="button" onClick={() => history.replace('/calculator')}>
                    Go to Calculator
                </button>
            </section>
        );
    }

    const assumptionRows = [
        { label: 'Monthly leads', value: inputs.monthlyLeads.toLocaleString() },
        { label: 'Booking rate', value: formatPercentage(inputs.bookingRate) },
        { label: 'Show rate', value: formatPercentage(inputs.showRate) },
        { label: 'Close rate', value: formatPercentage(inputs.closeRate) },
        { label: 'Average sale value', value: formatCurrency(inputs.averageOrderValue) },
        { label: 'Retention rate', value: formatPercentage(inputs.retentionRate) },
        { label: 'Retention months', value: inputs.retentionMonths.toLocaleString() },
        { label: 'Marketing spend', value: formatCurrency(inputs.marketingSpend) },
        { label: 'Other costs', value: formatCurrency(inputs.fixedCosts) },
    ];

    return (
        <section className="results-page">
            <h1>ROI Results</h1>
            <p className="page-subtitle">Detailed breakdown of your monthly appointment economics.</p>

            <div className="results-summary-grid">
                <div className="summary-card">
                    <span className="summary-label">ROI</span>
                    <span className="summary-value">{formatPercentage(results.roi)}</span>
                    <p className="summary-subtext">Return on total monthly spend</p>
                </div>
                <div className="summary-card">
                    <span className="summary-label">Net Profit</span>
                    <span className="summary-value">{formatCurrency(results.netProfit)}</span>
                    <p className="summary-subtext">After marketing and operating costs</p>
                </div>
                <div className="summary-card">
                    <span className="summary-label">Total Revenue</span>
                    <span className="summary-value">{formatCurrency(results.totalRevenue)}</span>
                    <p className="summary-subtext">Includes retention contribution</p>
                </div>
                <div className="summary-card">
                    <span className="summary-label">Cost per acquisition</span>
                    <span className="summary-value">
                        {results.costPerAcquisition ? formatCurrency(results.costPerAcquisition) : '—'}
                    </span>
                    <p className="summary-subtext">Average cost to win one new client</p>
                </div>
            </div>

            <div className="results-breakdown">
                <h2>Performance Funnel</h2>
                <ul className="funnel-list">
                    <li>
                        <strong>Booked appointments:</strong> {Math.round(results.bookedAppointments).toLocaleString()}
                    </li>
                    <li>
                        <strong>Completed appointments:</strong>{' '}
                        {Math.round(results.completedAppointments).toLocaleString()}
                    </li>
                    <li>
                        <strong>Closed clients:</strong> {Math.round(results.closedClients).toLocaleString()}
                    </li>
                </ul>

                <h2>Revenue Breakdown</h2>
                <ul className="revenue-list">
                    <li>
                        <strong>Net new revenue:</strong> {formatCurrency(results.netNewRevenue)}
                    </li>
                    <li>
                        <strong>Retention revenue:</strong> {formatCurrency(results.retentionRevenue)}
                    </li>
                    <li>
                        <strong>Total revenue:</strong> {formatCurrency(results.totalRevenue)}
                    </li>
                    <li>
                        <strong>Total costs:</strong> {formatCurrency(results.totalCost)}
                    </li>
                    <li>
                        <strong>Net profit:</strong> {formatCurrency(results.netProfit)}
                    </li>
                </ul>
            </div>

            <div className="assumptions">
                <h2>Scenario Inputs</h2>
                <dl>
                    {assumptionRows.map((row) => (
                        <div key={row.label} className="assumption-row">
                            <dt>{row.label}</dt>
                            <dd>{row.value}</dd>
                        </div>
                    ))}
                </dl>
            </div>

            <div className="results-actions">
                <button type="button" onClick={() => history.push('/calculator')}>
                    Adjust Scenario
                </button>
            </div>
        </section>
    );
};

export default Results;
